<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('SubNodeTypes.subnodeComponentType') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="isFetching"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <TableRemote
        v-if="hasPermission('nodes.types.view')"
        :iconHeader="hasPermission('nodes.types.add') ? 'add' : null"
        :items="displayedComponentTypes"
        isSearchable
        isCustomIcon
        :headers="tableHeaders"
        :itemActions="actions"
        :title="$t('SubNodeTypes.subnodeComponentType')"
        :pagination="pagination"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        :sortableColumns="['name', 'type']"
        hideDefaultFooter
        @iconAction="onClickAction"
        @sendQueryParams="fetchQueryParams"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </TableRemote>
      <NoPermissionAlert
        v-else
        :message="$t('permissions.error')"
      />
    </template>
  </BoxWithTitle>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import ComponentForm from './ComponentForm.vue';

export default {
    components: {
        BoxWithTitle,
        NoPermissionAlert,
        TableRemote,
    },
    data() {
        return {
            componentTypes: [],
            componentTypeKeys: [],
            pagination: {},
            isFetching: false,
            queryParams: { perpage: 25, page: 1 },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',

        ]),
        tableHeaders() {
            return [
                'name',
                'type',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedComponentTypes() {
            return this.componentTypes.map(componentType => ({
                id: componentType.id,
                name: componentType.name[this.userLang] || componentType.name[this.teamMainLang],
                type: componentType.key,

            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('nodes.types.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('nodes.types.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        await this.getComponentTypes();
    },
    methods: {
        fetchQueryParams(queryParams) {
            if (!isEmpty(queryParams.order)) {
                const split = queryParams.order.split('_');
                const [filedName, sort] = split;
                // eslint-disable-next-line no-param-reassign
                if (filedName === 'type') queryParams.order = `key_${sort}`;
            }
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.getComponentTypes();
        },
        async getComponentTypes(nextPage = 1) {
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getComponentTypes(this.queryParams);
                this.componentTypes = [...data.componentTypes];
                this.pagination = data.pagination;
                this.isFetching = false;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }

            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getComponentTypes(this.queryParams);
            this.componentTypes = [...this.componentTypes, ...data.componentTypes];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('SubNodeTypes.addComponentType'),
                component: Vue.component('componentForm', ComponentForm),
                payload: { usedKeys: this.componentTypeKeys },
                showLangs: true,
                onConfirm: requestPayload => this.addComponent(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getComponentType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.updateComponentType'),
                    component: Vue.component('ComponentForm', ComponentForm),
                    showLangs: true,
                    isForm: false,
                    payload: { componentType: data, usedKeys: this.componentTypeKeys, isEdition: true },
                    onConfirm: requestPayload => this.updateComponent(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.removeComponentType'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    isForm: false,
                    payload: { msg: this.$t('SubNodeTypes.removeComponentType') },
                    onConfirm: () => this.removeComponent(item.id),
                });
            }
        },

        async addComponent(requestPayload) {
            await this.$rq.addComponentType(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.added'),
            });
            await this.getComponentTypes();
        },
        async updateComponent(id, requestPayload) {
            await this.$rq.updateComponentType(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.updated'),
            });
            await this.getComponentTypes();
        },
        async removeComponent(id) {
            await this.$rq.removeComponentType(id);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.removed'),
            });
            await this.getComponentTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getComponentTypes(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
